import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSeo } from '@/lib/seo';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, PackageCheck, RotateCcw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const REASONS = [
  { value: 'faulty', label: 'Faulty / defective' },
  { value: 'damaged', label: 'Damaged in transit' },
  { value: 'misdescribed', label: 'Not as described' },
  { value: 'wrong_item', label: 'Wrong item received' },
  { value: 'changed_mind', label: 'Changed my mind (14-day cooling-off)' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  received: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  refunded: 'bg-green-500/10 text-green-400 border-green-500/20',
  cancelled: 'bg-white/5 text-white/40 border-white/10',
};

const STATUS_LABELS = {
  pending: 'Pending Review',
  approved: 'Approved — Awaiting Return',
  rejected: 'Rejected',
  received: 'Return Received',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
};

const EMPTY = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  order_number: '',
  product_name: '',
  reason: 'changed_mind',
  return_type: 'refund',
  description: '',
};

export default function Returns() {
  useSeo({
    title: 'Returns & RMA — Motion Reality Sim Racing Ltd',
    description: 'Request a return or refund for an order from Motion Reality Sim Racing Ltd.',
  });
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [lookupEmail, setLookupEmail] = useState('');
  const [searched, setSearched] = useState(false);

  const { data: myRequests = [], isFetching } = useQuery({
    queryKey: ['rma-lookup', lookupEmail],
    enabled: searched && !!lookupEmail,
    queryFn: () => base44.entities.RmaRequest.filter({ customer_email: lookupEmail }, '-created_date'),
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const created = await base44.entities.RmaRequest.create(form);
      setSubmittedId(created.id);
      setForm(EMPTY);
      toast({ title: 'Return request submitted', description: 'We will be in touch shortly.' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not submit', description: err?.message || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const runLookup = (e) => {
    e.preventDefault();
    setSearched(true);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">RETURNS</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
              Returns & RMA
            </h1>
            <p className="text-white/55 text-sm leading-relaxed max-w-xl">
              Need to return an item? Submit a Return Merchandise Authorisation (RMA) request below and our team will review it within 1–2 working days. Please review our <Link to="/refund-policy" className="text-primary hover:text-primary/80 underline">Refund Policy</Link> before submitting.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-14">
        <div className="max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-[#111115] border border-white/5 p-7">
            <div className="flex items-center gap-2 mb-6">
              <RotateCcw className="w-4 h-4 text-primary" />
              <h2 className="text-white font-bold text-sm tracking-widest uppercase">Submit a Return</h2>
            </div>

            {submittedId ? (
              <div className="text-center py-8">
                <PackageCheck className="w-10 h-10 text-primary mx-auto mb-4" />
                <p className="text-white font-bold mb-1">Request Received</p>
                <p className="text-white/40 text-xs mb-1">Your RMA reference is:</p>
                <p className="text-primary font-mono text-sm break-all">{submittedId}</p>
                <button
                  onClick={() => setSubmittedId(null)}
                  className="mt-6 text-white/50 hover:text-white text-xs font-bold tracking-widest uppercase"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Full Name *">
                    <input name="customer_name" value={form.customer_name} onChange={handleChange} required className={inputCls} />
                  </Field>
                  <Field label="Email *">
                    <input type="email" name="customer_email" value={form.customer_email} onChange={handleChange} required className={inputCls} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Phone">
                    <input name="customer_phone" value={form.customer_phone} onChange={handleChange} className={inputCls} />
                  </Field>
                  <Field label="Order Number">
                    <input name="order_number" value={form.order_number} onChange={handleChange} className={inputCls} />
                  </Field>
                </div>
                <Field label="Product Name *">
                  <input name="product_name" value={form.product_name} onChange={handleChange} required className={inputCls} />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Reason *">
                    <select name="reason" value={form.reason} onChange={handleChange} className={inputCls}>
                      {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Requesting *">
                    <select name="return_type" value={form.return_type} onChange={handleChange} className={inputCls}>
                      <option value="refund">Refund</option>
                      <option value="replacement">Replacement</option>
                    </select>
                  </Field>
                </div>
                <Field label="Details">
                  <textarea name="description" value={form.description} onChange={handleChange} rows={4} className={inputCls} placeholder="Describe the issue or reason for return..." />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-bold text-sm py-3.5 transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? 'SUBMITTING...' : 'SUBMIT RETURN REQUEST'}
                </button>
              </form>
            )}
          </motion.div>

          {/* Lookup */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#111115] border border-white/5 p-7">
            <div className="flex items-center gap-2 mb-6">
              <Search className="w-4 h-4 text-primary" />
              <h2 className="text-white font-bold text-sm tracking-widest uppercase">Track Your Requests</h2>
            </div>
            <form onSubmit={runLookup} className="flex gap-2 mb-6">
              <input
                type="email"
                value={lookupEmail}
                onChange={(e) => setLookupEmail(e.target.value)}
                placeholder="Your email address"
                className={inputCls}
              />
              <button type="submit" className="bg-primary hover:bg-primary/90 text-white font-bold text-xs px-5 transition-colors whitespace-nowrap">
                FIND
              </button>
            </form>

            {!searched && (
              <p className="text-white/30 text-sm text-center py-8">
                Enter the email you used to submit a return to view its status.
              </p>
            )}
            {searched && isFetching && (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            )}
            {searched && !isFetching && myRequests.length === 0 && (
              <p className="text-white/30 text-sm text-center py-8">No return requests found for this email.</p>
            )}
            {searched && !isFetching && myRequests.length > 0 && (
              <div className="space-y-3">
                {myRequests.map((r) => (
                  <div key={r.id} className="border border-white/5 p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-white font-bold text-sm">{r.product_name}</p>
                        <p className="text-white/30 text-xs font-mono break-all">{r.id}</p>
                      </div>
                      <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 border ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>
                    {r.return_tracking_number && (
                      <p className="text-white/40 text-xs mt-2">Return tracking: <span className="text-white/60">{r.return_tracking_number}</span></p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/50 transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest uppercase mb-1.5">{label}</label>
      {children}
    </div>
  );
}